import { useMemoizedFn, useMount } from 'ahooks';
import { inViewerUnit, reexpress } from 'utils/measurement';
import { Checkbox, Col, Form, Input, Row } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useAppDispatch } from 'app/hooks';
import { DeleteIcon } from 'components/Icons';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMeasureNumberInput } from 'components/RevxMeasureInput/RevxMeasureNumberInput';
import { MeasureType } from 'domains/measurementDomain';
import {
  omitUntouchedMeasurements,
  useMeasurementUnit,
} from 'hooks/useMeasurementUnit';
import { UnderlineText } from 'components/RevxTypography';
import {
  CustomRuleCodeEnum,
  OrientationEnum,
  SkuSize,
  SkuSizePayload,
  StepActionTypeEnum,
  UpdateSize,
} from 'domains/skuDomain';
import { Dispatch, Fragment, SetStateAction, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { organizeStructure } from 'utils/filter';
import { plainOptionHandle } from 'utils/jobConfig';
import { RULE_MEASURE_PRECISION } from 'utils/validators';
import { StepComponentProps } from '../SingleWizardStep';
import '../SingleWizardStep/Step2Size.less';
import { FormCustomRuleCode } from './FormCustomRuleCode';
import { SkuPresetSizePicker } from './SkuPresetSizePicker';

type CalculateSkuSize = {
  idx?: number;
  skuSize?: Partial<SkuSizePayload>;
};

interface StepSizeProps extends StepComponentProps {
  setDraftSize: Dispatch<SetStateAction<SkuSize[] | undefined>>;
  skipSizeAndOrientation: boolean;
}

export const SignleSize = ({
  sku,
  topSku,
  skuPart,
  setDraftSize,
  skipSizeAndOrientation,
}: StepSizeProps) => {
  const { t } = useTranslation();
  const { columnTitle, metric, sizeFor, unit } = useMeasurementUnit();
  const [form] = useForm();
  const dispatch = useAppDispatch();
  // Judged on what the user sees: in metric the form still holds full-precision inches.
  const precisionRule = RULE_MEASURE_PRECISION(3, MeasureType.LENGTH, form);

  const formItemLayout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 18 },
  };

  const tailFormItemLayout = {
    wrapperCol: {
      span: 18,
      offset: 6,
    },
  };

  const formTableItemLayout = {
    wrapperCol: {
      span: 24,
    },
  };

  useMount(() => {
    form.setFieldsValue({
      // Both selections ride in the form so the one the supplier is not looking at is sent back
      // unchanged rather than cleared.
      presetSizesMetric: sku.presetSizesMetric ?? [],
      presetSizesImperial: sku.presetSizesImperial ?? [],
      customSizeEnable: false,
      ...inViewerUnit(sku, unit),
      skuSizes: defaultSize(),
      customRuleCode: sku.customRuleCode || CustomRuleCodeEnum.NONE,
    });

    dispatch.sku.registerStepAction({
      [StepActionTypeEnum.STEP2_SIZES]: submit,
    });
  });



  const defaultSize = useCallback(() => {
    const filterSkuSizes = (sku.skuSizes || []).filter((item) =>
      sku.orientation?.includes(item.orientation)
    );
    let sizeValue: Partial<SkuSizePayload>[] = filterSkuSizes.map(
      (item, index) => {
        return {
          finishedWidth: organizeStructure(sizeFor(item.finishedSize, item.finishedSizeMetric) || "").width,
          finishedLength: organizeStructure(sizeFor(item.finishedSize, item.finishedSizeMetric) || "").length,
          flatWidth: organizeStructure(sizeFor(item.flatSize, item.flatSizeMetric) || "").width,
          flatLength: organizeStructure(sizeFor(item.flatSize, item.flatSizeMetric) || "").length,
          orientation: item.orientation || undefined,
          key: index,
        };
      }
    );

    if (!sizeValue || !sizeValue?.length) {
      sizeValue = [
        {
          key: 1,
        },
      ];
    }

    return sizeValue;
  }, [sku.skuSizes, sku.orientation, sizeFor]);

  useEffect(() => {
    const before = form.getFieldValue('skuSizes');
    const skuSizeData = before?.filter(
      (item: SkuSizePayload) =>
        item?.orientation && sku.orientation?.includes(item?.orientation)
    );
    // Rows dropped because their orientation is no longer offered. Nobody typed anything, but
    // the table is no longer what is stored, so the save has to say so - see `sizeRowsEdited`.
    if (before && skuSizeData && skuSizeData.length !== before.length) {
      sizeRowsEdited.current = true;
    }
    form.setFieldsValue({
      skuSizes:
        skuSizeData && skuSizeData.length
          ? skuSizeData
          : [
              {
                key: 1,
                orientation: sku.orientation?.includes(OrientationEnum.portrait)
                  ? OrientationEnum.portrait
                  : OrientationEnum.landscape,
              },
            ],
    });
  }, [sku.orientation, form]);

  /**
   * Whether anyone actually changed the size table, as opposed to walking past it.
   *
   * The table is filled from the record on mount and re-expressed in place on every unit
   * switch, so what the form holds is always what the reader is being shown - and in metric
   * that is rounded to three decimals. Re-sent regardless, a stored 8.333in came back as
   * 8.332992in after one metric "Next", and a little further out on the next. The API leaves
   * the stored sizes alone when a request does not mention them, so an untouched table simply
   * is not mentioned.
   *
   * antd only reports a user's own edits through `onValuesChange`, so the row-count changes
   * below set this by hand.
   */
  const sizeRowsEdited = useRef(false);

  const addSize = () => {
    sizeRowsEdited.current = true;
    const val = form.getFieldsValue() || {};
    if (!val.skuSizes) {
      val.skuSizes = [];
    }
    val.skuSizes.push({
      key: (val.skuSizes || []).length,
      orientation: OrientationEnum.portrait,
    });
    form.setFieldsValue(val);
  };

  const submit = useMemoizedFn(async () => {
    if (sku.readonly || topSku) {
      return true;
    }
    if (!sku.orientation || !sku.orientation.length) {
      return false;
    }
    try {
      const formValue = await form.validateFields();
      if (formValue.customRuleCode === CustomRuleCodeEnum.NONE) {
      }
      // Left out entirely when nobody touched the table - see `sizeRowsEdited`.
      const { skuSizes, ...restOfForm } = formValue;
      const editedSizes = sizeRowsEdited.current ? { skuSizes } : {};
      const res = await dispatch.sku.updateSize(
        // Untouched measurements are left out, not re-sent. The bleed is shown in the
        // unit being read, so a metric viewer who never touched it would post 1.588mm
        // back for a stored 0.0625in and get 0.062520in - a little worse on every save.
        // The API leaves out what a request does not mention.
        omitUntouchedMeasurements(
          {
            id: sku.id,
            version: sku.version,
            // The unit these numbers are in, which is the one the form is displaying.
            enteredUnit: unit,
            // In the unit this payload declares - sending the stored imperial value under a
            // metric declaration is what rounded the bleed down to zero. Dropped again below
            // when the user never touched it, so it cannot drift.
            defaultBleedSizeIn: inViewerUnit(sku, unit)?.defaultBleedSizeIn,
            orientation: sku.orientation,
            skipSizeAndOrientation: skipSizeAndOrientation,
            ...restOfForm,
            ...editedSizes,
          },
          form
        )
      );
      return res;
    } catch {
      // do nothing when form invalid
      return false;
    }
  });

  const formValuesChange = (
    values: Partial<UpdateSize>,
    allvalue: UpdateSize
  ) => {
    if (values.skuSizes) {
      sizeRowsEdited.current = true;
    }
    let calculateSkuSize: CalculateSkuSize = {};
    (values.skuSizes || []).forEach(
      (item: Partial<SkuSizePayload>, i: number) => {
        if (item) {
          if (
            ['finishedWidth', 'finishedLength'].includes(Object.keys(item)[0])
          ) {
            if (
              item.finishedWidth &&
              (allvalue.skuSizes || [])[i].finishedLength
            ) {
              calculateSkuSize.idx = i;
              calculateSkuSize.skuSize = plainOptionHandle[
                allvalue.customRuleCode
              ](
                {
                  finishedWidth: item.finishedWidth,
                  finishedLength: (allvalue.skuSizes || [])[i].finishedLength,
                },
                allvalue.foldPanelCount || 0
              );
            } else if (
              item.finishedLength &&
              (allvalue.skuSizes || [])[i].finishedWidth
            ) {
              calculateSkuSize.idx = i;
              calculateSkuSize.skuSize = plainOptionHandle[
                allvalue.customRuleCode
              ](
                {
                  finishedWidth: (allvalue.skuSizes || [])[i].finishedWidth,
                  finishedLength: item.finishedLength,
                },
                allvalue.foldPanelCount || 0
              );
            }
          } else if (
            ['flatWidth', 'flatLength'].includes(Object.keys(item)[0])
          ) {
            if (item.flatWidth && (allvalue.skuSizes || [])[i].flatLength) {
              calculateSkuSize.idx = i;
              calculateSkuSize.skuSize = plainOptionHandle[
                allvalue.customRuleCode
              ](
                {
                  flatWidth: item.flatWidth,
                  flatLength: (allvalue.skuSizes || [])[i].flatLength,
                },
                allvalue.foldPanelCount || 0
              );
            } else if (
              item.flatLength &&
              (allvalue.skuSizes || [])[i].flatWidth
            ) {
              calculateSkuSize.idx = i;
              calculateSkuSize.skuSize = plainOptionHandle[
                allvalue.customRuleCode
              ](
                {
                  flatWidth: (allvalue.skuSizes || [])[i].flatWidth,
                  flatLength: item.flatLength,
                },
                allvalue.foldPanelCount || 0
              );
            }
          }
        }
      }
    );
    if (
      typeof calculateSkuSize.idx !== 'undefined' &&
      calculateSkuSize.skuSize
    ) {
      allvalue.skuSizes.splice(calculateSkuSize.idx, 1, {
        finishedWidth: calculateSkuSize.skuSize.finishedWidth || 0,
        finishedLength: calculateSkuSize.skuSize.finishedLength || 0,
        flatWidth: calculateSkuSize.skuSize.flatWidth || 0,
        flatLength: calculateSkuSize.skuSize.flatLength || 0,
        orientation:
          calculateSkuSize.skuSize.orientation || OrientationEnum.portrait,
      });
      form.setFieldsValue(allvalue);
    }
    console.log(allvalue.skuSizes);
    if (values.customRuleCode) {
      // The fold rule rewrites every row's flat size, so the table no longer matches what is
      // stored even though nobody typed in it. Without this the recomputed sizes were dropped
      // from the payload and the change was silently lost - `sizeRowsEdited` is set from
      // `values.skuSizes`, and this branch fires on a different field.
      sizeRowsEdited.current = true;
      const skuSizes = (allvalue.skuSizes || []).map((item) => {
        if (item.finishedWidth && item.finishedLength) {
          return plainOptionHandle[allvalue.customRuleCode](
            {
              finishedWidth: item.finishedWidth,
              finishedLength: item.finishedLength,
            },
            allvalue.foldPanelCount || 0
          );
        } else if (item.flatWidth && item.flatLength) {
          return plainOptionHandle[allvalue.customRuleCode](
            {
              flatWidth: item.flatWidth,
              flatLength: item.flatLength,
            },
            allvalue.foldPanelCount || 0
          );
        } else {
          return item;
        }
      });
      form.setFieldsValue({
        ...allvalue,
        skuSizes: skuSizes,
      });
    }

    const validate = form.getFieldsError();
    if (validate.filter((item) => item.errors && item.errors.length).length) {
      form.validateFields();
    }

    if (values.skuSizes) {
      const res = allvalue.skuSizes.map((item) => {
        return {
          finishedSize: `${item.finishedWidth || 0}x${
            item.finishedLength || 0
          }`,
          flatSize: `${item.flatWidth || 0}x${item.flatLength || 0}`,
          orientation: item.orientation,
          widthLengthSize: '',
        };
      });
      setDraftSize(res);
    }
  };

  const removeItem = (idx: number) => {
    sizeRowsEdited.current = true;
    const val = form.getFieldsValue() || {};

    val.skuSizes.splice(idx, 1);

    form.setFieldsValue(val);

    const validate = form.getFieldsError();
    if (validate.filter((item) => item.errors && item.errors.length).length) {
      form.validateFields();
    }
  };

  // Re-express the form's own numbers when the viewer switches units.
  //
  // Converted from what the form currently holds, not re-derived from the record: that keeps rows
  // the user has just added and edits they have not saved, and does not depend on the API having
  // sent a companion for every value. A stale form is what wrote metric numbers into the imperial
  // column - the page was switched to imperial while the table still held millimetres, and the
  // save recorded them faithfully as inches.
  const shownInUnit = useRef(unit);
  useEffect(() => {
    if (shownInUnit.current === unit) {
      return;
    }
    const from = shownInUnit.current;
    shownInUnit.current = unit;
    const current = form.getFieldsValue();
    form.setFieldsValue({
      skuSizes: (current.skuSizes || []).map((row: Record<string, unknown>) =>
        row
          ? {
              ...row,
              finishedWidth: reexpress(row.finishedWidth as number, MeasureType.LENGTH, from, unit),
              finishedLength: reexpress(row.finishedLength as number, MeasureType.LENGTH, from, unit),
              finishedHeight: reexpress(row.finishedHeight as number, MeasureType.LENGTH, from, unit),
              flatWidth: reexpress(row.flatWidth as number, MeasureType.LENGTH, from, unit),
              flatLength: reexpress(row.flatLength as number, MeasureType.LENGTH, from, unit),
            }
          : row
      ),
      defaultBleedSizeIn: reexpress(
        current.defaultBleedSizeIn,
        MeasureType.LENGTH,
        from,
        unit
      ),
    });
  }, [unit, form]);

  return (
    <>
      <Form
        form={form}
        className="single-sku-config-sizes"
        {...formItemLayout}
        onValuesChange={formValuesChange}
      >
        <Form.Item label={'Custom rule code'} name="customRuleCode">
          <FormCustomRuleCode disabled={Boolean(topSku) || sku.readonly} />
        </Form.Item>
        <Form.Item
          label={t('platform.sku.wizard.size.form.label.foldPanelCount')}
          name="foldPanelCount"
          rules={[{ required: true }]}
        >
          <RevxInputNumber
            min={0}
            isInteger
            disabled={sku.readonly || Boolean(topSku)}
          />
        </Form.Item>
        <Row>
          <Col span={6} className={'ant-form-item-label'}>
            <label>Sizes</label>
          </Col>
          <Col span={18}>
            <table className="size-table">
              <thead>
                <tr>
                  <th rowSpan={2}>
                    {t(
                      'platform.sku.wizard.size.form.label.landscapePortraitSize'
                    )}
                    ?
                  </th>
                  <th colSpan={2}>
                    {columnTitle(
                      t('platform.sku.wizard.size.form.label.finishedSize'),
                      MeasureType.LENGTH
                    )}
                  </th>
                  <th colSpan={2}>
                    {columnTitle(
                      t(
                        'platform.sku.wizard.size.form.label.flatSizeIncludesLines'
                      ),
                      MeasureType.LENGTH
                    )}{' '}
                  </th>
                </tr>
                <tr>
                  <th>{t('common.width')}</th>
                  <th>{t('common.length')}</th>
                  <th>{t('common.width')}</th>
                  <th>{t('common.length')}</th>
                </tr>
              </thead>
              <tbody>
                <Form.List
                  name={'skuSizes'}
                  rules={[
                    {
                      validator: async (_, skuSizes) => {
                        const skuSizeArr = skuSizes.map(
                          (size: Partial<SkuSizePayload>) =>
                            `${size.finishedLength}${size.finishedWidth}${size.orientation}`
                        );
                        if (new Set(skuSizeArr).size !== skuSizeArr.length) {
                          return Promise.reject(
                            new Error('Sku size must be unique')
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  {(fields, { add, remove }, { errors }) =>
                    fields.map((field, idx) => (
                      <Fragment key={field.key}>
                        <tr>
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, 'orientation']}
                              fieldKey={[field.fieldKey, 'orientation']}
                              rules={[
                                ({ getFieldValue }) => ({
                                  validator(_, value) {
                                    if (sku.orientation?.includes(value)) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject(
                                      new Error(
                                        'Size contains unsupported orientation'
                                      )
                                    );
                                  },
                                }),
                              ]}
                              {...formTableItemLayout}
                            >
                              <Input disabled />
                            </Form.Item>
                          </td>
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, 'finishedWidth']}
                              fieldKey={[field.fieldKey, 'finishedWidth']}
                              rules={[
                                { required: true },
                                { type: 'number', min: 0 },
                                precisionRule,
                              ]}
                              {...formTableItemLayout}
                            >
                              <RevxMeasureNumberInput
                                type={MeasureType.LENGTH}
                                min={0}
                                disabled={sku.readonly || Boolean(topSku)}
                              />
                            </Form.Item>
                          </td>
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, 'finishedLength']}
                              fieldKey={[field.fieldKey, 'finishedLength']}
                              rules={[
                                { required: true },
                                { type: 'number', min: 0 },
                                precisionRule,
                              ]}
                              {...formTableItemLayout}
                            >
                              <RevxMeasureNumberInput
                                type={MeasureType.LENGTH}
                                min={0}
                                disabled={sku.readonly || Boolean(topSku)}
                              />
                            </Form.Item>
                          </td>
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, 'flatWidth']}
                              fieldKey={[field.fieldKey, 'flatWidth']}
                              rules={[
                                { required: true },
                                { type: 'number', min: 0 },
                                precisionRule,
                              ]}
                              {...formTableItemLayout}
                            >
                              <RevxMeasureNumberInput
                                type={MeasureType.LENGTH}
                                min={0}
                                disabled={sku.readonly || Boolean(topSku)}
                              />
                            </Form.Item>
                          </td>
                          <td>
                            <Form.Item
                              {...field}
                              label={''}
                              name={[field.name, 'flatLength']}
                              fieldKey={[field.fieldKey, 'flatLength']}
                              rules={[
                                { required: true },
                                { type: 'number', min: 0 },
                                precisionRule,
                              ]}
                              {...formTableItemLayout}
                            >
                              <RevxMeasureNumberInput
                                type={MeasureType.LENGTH}
                                min={0}
                                disabled={sku.readonly || Boolean(topSku)}
                              />
                            </Form.Item>
                          </td>
                          {fields.length > 1 ? (
                            <td
                              hidden={sku.readonly || Boolean(topSku)}
                              className="dynamic-delete-button"
                              onClick={() => removeItem(idx)}
                            >
                              <DeleteIcon />
                            </td>
                          ) : null}
                        </tr>
                        {fields.length - 1 === field.name &&
                          errors &&
                          !!errors.length && (
                            <tr style={{ border: 'none' }}>
                              <td style={{ border: 'none' }} colSpan={5}>
                                <Form.ErrorList
                                  className="form-list-error"
                                  errors={errors}
                                />
                              </td>
                            </tr>
                          )}
                      </Fragment>
                    ))
                  }
                </Form.List>
              </tbody>
            </table>
            {!sku.readonly && !Boolean(topSku) && (
              <UnderlineText
                color="blue"
                size="small"
                style={{ width: 90 }}
                ellipsis
                onClick={() => addSize()}
              >
                + {t('platform.sku.wizard.size.addSize')}
              </UnderlineText>
            )}
          </Col>
        </Row>
        {/* Its own row, so the heading lines up with the Sizes label above it. Which selection
            is being edited follows the unit, so the field is remounted on a switch rather than
            left bound to the other one. */}
        <Form.Item
          noStyle
          // The catalogue this reader is working in. The buyer's size step is fetched with the
          // same unit, so the list that gets filled here is the list that gets read there.
          key={metric ? 'presetSizesMetric' : 'presetSizesImperial'}
          name={metric ? 'presetSizesMetric' : 'presetSizesImperial'}
        >
          <SkuPresetSizePicker disabled={sku.readonly || Boolean(topSku)} />
        </Form.Item>

        <Form.Item
          label={''}
          {...tailFormItemLayout}
          name="customSizeEnable"
          valuePropName="checked"
        >
          <Checkbox disabled={sku.readonly || Boolean(topSku)}>
            <span>
              {t('platform.sku.wizard.size.form.label.allowCustomTip')}
            </span>
            <p>{t('platform.sku.wizard.size.form.label.maxiumSizeTip')}</p>
          </Checkbox>
        </Form.Item>

        <Form.Item
          // Named with its unit, like the size columns beside it. Without it the field held
          // millimetres under a bare label, and the only clue was the number getting smaller.
          label={columnTitle(
            t('platform.sku.wizard.size.form.label.bleedSize'),
            MeasureType.LENGTH
          )}
          name="defaultBleedSizeIn"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (((('' + value).split('.') || [])[1] || []).length < 4) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(
                    t('platform.sku.wizard.size.form.label.bleedSizePrecision')
                  )
                );
              },
            }),
          ]}
        >
          <RevxInputNumber min={0} disabled={sku.readonly || Boolean(topSku)} />
        </Form.Item>
      </Form>
    </>
  );
};
